import * as React from 'react';
import { useEffect } from 'react';

import { PageConfig_PageName as PageName } from '__generated__/graphql-types';

import requestUrlInfo from 'js/lib/requestUrlInfo';
import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import DomainListProvider from 'bundles/browse-common/providers/DomainListProvider';
import RelatedSkillsProvider from 'bundles/browse-common/providers/RelatedSkillsProvider';
import OnboardingV3Provider from 'bundles/onboarding-common/providers/OnboardingV3Provider';
import ComponentsConfigProvider from 'bundles/page-config-common/providers/ComponentsConfigProvider';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import { getShouldShowBrowseInSERP } from 'bundles/search-common/utils/experimentUtils';
import { isFreeQuery } from 'bundles/search-common/utils/pageConfigUtils';
import SeoSearchPageRootContent from 'bundles/seo-entity-page/components/SeoSearchPageRootContent';
import { SeoSearchProviderWrapper } from 'bundles/seo-entity-page/components/SeoSearchProviderWrapper';
import SeoPathConfigurationQueryWrapper from 'bundles/seo/api/SeoPathConfigurationQueryWrapper';
import { RedirectError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

const useAuthenticatedUserRedirect = () => {
  const { search } = useLocation();

  if (isAuthenticatedUser() && inServerContext) {
    const searchPageUrl = `/search${search}`;
    throw new RedirectError(302, searchPageUrl);
  }

  useEffect(() => {
    if (isAuthenticatedUser() && !inServerContext) {
      const searchPageUrl = `/search${search}`;
      window.location.replace(searchPageUrl);
    }
  }, [search]);
};

export const PageConfigSeoSearchPage = () => {
  useAuthenticatedUserRedirect();

  const { pathname, search, query } = useLocation();
  const { query: searchQueryFromParams } = query;
  const hostname = requestUrlInfo().hostname;
  const inferredQueryFromParams: string = inferQueryFromParams(searchQueryFromParams);

  // use free data from SEOConfiguration for all free queries
  const urlParams = new URLSearchParams(search);
  const originalQuery = urlParams.get('query') || '';
  const finalQuery = isFreeQuery(originalQuery) ? 'free' : originalQuery;
  if (finalQuery) urlParams.set('query', finalQuery);
  const path = hostname + pathname + '?' + urlParams.toString();

  return (
    <ComponentsConfigProvider pageName={PageName.Eqp} additionalInput={{ query_term: inferredQueryFromParams }}>
      <SeoPathConfigurationQueryWrapper path={path} shouldNotSkipIfAuthenticated={getShouldShowBrowseInSERP()}>
        <SeoSearchProviderWrapper isSEOEntityPage={true}>
          <RelatedSkillsProvider>
            <DomainListProvider>
              <OnboardingV3Provider>
                <SeoSearchPageRootContent />
              </OnboardingV3Provider>
            </DomainListProvider>
          </RelatedSkillsProvider>
        </SeoSearchProviderWrapper>
      </SeoPathConfigurationQueryWrapper>
    </ComponentsConfigProvider>
  );
};

export default PageConfigSeoSearchPage;
